import { canAccessEmployeeModule } from '../utils/employeeAccess';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import { canRead, canReadAny, canWrite, defaultPathForUser, roleLabel } from '../utils/roles';
import ErrorBoundary from './ErrorBoundary';
import haptic from '../utils/haptic';
import AiRulesQuickPopup from './AiRulesQuickPopup';
import DeployNotice from './DeployNotice';
import './Layout.css';

const RRHH_MODULE_RESOURCES = [
  'rrhh.plan-semanal', 'rrhh.plan-mensual',
  'rrhh.tareas',
  'rrhh.incidencias',
  'rrhh.jornales',
  'rrhh.operaciones',
  'rrhh.dashboard',
  'rrhh.reportes',
  'rrhh.analisis',
  'rrhh.mis-marcas',
  'rrhh.configuracion',
];

const SUELDOS_MODULE_RESOURCES = [
  'configuracion.personal',
  'rrhh.liquidacion',
  'rrhh.remuneraciones',
  'rrhh.liquidacion-parametros',
  'rrhh.liquidacion-cierre',
  'rrhh.configuracion',
];

const OPERATIONAL_MODULE_RESOURCES = [
  'agenda',
  'agenda.equipos',
  'agenda.equipos.evento',
  'logistica',
  'lugares-activos',
  'inventario.stock',
  'inventario.racks',
  'inventario.packs',
  'inventario.incidencias',
  'ventas.leads',
  'ventas.cotizaciones',
  'ventas.clientes',
  'ventas.proveedores',
  'ventas.productos',
  'ventas.lugares',
];

// Cartel cuando una página falla — la app (sidebar, navegación) sigue viva.
function PageError() {
  return (
    <div style={{ padding: 40, maxWidth: 520 }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Algo salió mal en esta pantalla</h2>
      <p style={{ color: 'var(--text-dim)', fontSize: 14, marginBottom: 16 }}>
        Podés recargar la página o volver a otra sección desde el menú. El resto de la app sigue funcionando.
      </p>
      <button className="btn btn-primary" onClick={() => window.location.reload()}>Recargar</button>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const canUseRrhhModule = canReadAny(user, RRHH_MODULE_RESOURCES);
  const canUseSueldoModule = canAccessEmployeeModule(user) && canReadAny(user, SUELDOS_MODULE_RESOURCES);
  const canUseClock = canRead(user, 'rrhh.reloj');
  const canUseConfig = canReadAny(user, [
    'configuracion.personal',
    'configuracion.biometria',
    'configuracion.permisos',
    'configuracion.ia',
    'configuracion.sistema',
  ]);
  // La matriz, no la etiqueta de rol, define si esta persona puede navegar
  // módulos operativos. Así un Admin RRHH al que se le dé Agenda no queda
  // artificialmente encerrado en RRHH.
  const canUseOperationalModules = canReadAny(user, OPERATIONAL_MODULE_RESOURCES);

  useEffect(() => {
    const isAllowedRrhhPath = location.pathname.startsWith('/rrhh');
    const isAllowedSueldoPath = canUseSueldoModule && location.pathname.startsWith('/sueldos');
    const isAllowedConfigPath = canUseConfig && location.pathname.startsWith('/configuracion');
    if (!canUseOperationalModules && !isAllowedRrhhPath && !isAllowedSueldoPath && !isAllowedConfigPath) {
      navigate(defaultPathForUser(user), { replace: true });
    }
  }, [canUseConfig, canUseOperationalModules, canUseSueldoModule, location.pathname, navigate, user]);

  const [menuOpen, setMenuOpen] = useState(false);
  const swipeRef = useRef(null);

  // Deslizar para CERRAR el menú, nada más.
  //
  // El gesto para abrirlo se sacó: arrastrar desde el borde izquierdo es el
  // "atrás" del iPhone, así que el sistema y la app se peleaban por el mismo
  // movimiento. Se abre con el botón. Cerrar arrastrando hacia la izquierda no
  // choca con ningún gesto del sistema y sólo existe con el menú ya abierto,
  // que es cuando el overlay tapa todo y no hay nada más que pueda querer ese
  // arrastre.
  //
  // Se decide en touchmove, no en touchend: apenas el gesto se vuelve un
  // desplazamiento el navegador se queda con él y manda touchcancel en vez de
  // touchend, así que esperar al final es esperar algo que muchas veces no
  // llega.
  function onTouchStart(event) {
    if (!menuOpen || event.touches.length !== 1) { swipeRef.current = null; return; }
    const touch = event.touches[0];
    swipeRef.current = { x: touch.clientX, y: touch.clientY, resuelto: false };
  }

  function onTouchMove(event) {
    const inicio = swipeRef.current;
    if (!inicio || inicio.resuelto || !menuOpen || event.touches.length !== 1) return;
    const touch = event.touches[0];
    const dx = touch.clientX - inicio.x;
    const dy = touch.clientY - inicio.y;
    if (dx > -60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    inicio.resuelto = true;
    // En el gesto la confirmación importa más que en el botón: no hay nada
    // que tocar, así que el pulso es lo que avisa que el swipe fue tomado.
    haptic.untick();
    setMenuOpen(false);
  }

  function onTouchEnd() {
    swipeRef.current = null;
  }
  const [tooltip, setTooltip] = useState({ visible: false, label: '', y: 0 });

  // Globito de leads sin atender en el ícono de Ventas: se ve desde cualquier
  // pantalla. Sondeo cada 60s + al entrar/salir de la lista de leads (para que
  // se actualice apenas los atendés).
  const [newLeads, setNewLeads] = useState(0);
  const showLeadsBadge = canUseOperationalModules && canRead(user, 'ventas.leads');
  const onLeadsPage = location.pathname.startsWith('/ventas/leads');
  useEffect(() => {
    if (!showLeadsBadge) return;
    let alive = true;
    const load = () => api.get('/leads/stats')
      .then(r => { if (alive) setNewLeads(r.data?.nuevos || 0); })
      .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [showLeadsBadge, onLeadsPage]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const showTip = useCallback((e, label) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({ visible: true, label, y: rect.top + rect.height / 2 });
  }, []);

  const hideTip = useCallback(() => {
    setTooltip(t => ({ ...t, visible: false }));
  }, []);

  const nav = (to, end, label, icon, meta = 0) => {
    const badge = typeof meta === 'number' ? meta : 0;
    const options = typeof meta === 'object' ? meta : {};

    return (
      <NavLink
        to={to}
        end={end}
        className={({ isActive }) => {
          const active = options.isActive ? options.isActive(location.pathname, isActive) : isActive;
          return `nav-item${active ? ' active' : ''}`;
        }}
        onClick={() => setMenuOpen(false)}
        onMouseEnter={e => showTip(e, label)}
        onMouseLeave={hideTip}
      >
        {icon}
        {badge > 0 && <span className="nav-badge">{badge > 99 ? '99+' : badge}</span>}
        <span className="nav-item-text">{label}</span>
      </NavLink>
    );
  };

  return (
    <div
      className="layout"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
    >
      {menuOpen && (
        <div className="sidebar-overlay" onClick={() => setMenuOpen(false)} />
      )}

      <aside className={`sidebar${menuOpen ? ' open' : ''}`}>
        <div className="sidebar-logo">
          <img
            src={theme === 'light' ? '/logo2.png' : '/logo.png'}
            alt="Logo"
            className={`sidebar-logo-img${theme === 'light' ? ' logo-light' : ''}`}
          />
        </div>

        <nav className="sidebar-nav">
          {canUseOperationalModules && canRead(user, 'agenda') && nav('/', true, 'Agenda',
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
              <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
            </svg>
          )}

          {canUseOperationalModules && canReadAny(user, ['ventas.leads', 'ventas.cotizaciones', 'ventas.clientes', 'ventas.proveedores', 'ventas.productos', 'ventas.lugares']) && nav('/ventas', false, 'Ventas',
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23"/>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>,
            newLeads
          )}

          {canUseRrhhModule && nav('/rrhh', false, 'Horas',
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>,
            { isActive: (pathname, isActive) => isActive && !pathname.startsWith('/rrhh/reloj') }
          )}

          {(canUseSueldoModule || canUseConfig) && nav(canUseSueldoModule ? '/sueldos' : '/configuracion', false, 'Personal',
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="5" width="18" height="16" rx="2" />
              <path d="M7 3v4M17 3v4M3 10h18" />
              <path d="M12 12v6M14.25 13.5c-.45-.55-1.16-.82-2.12-.82-1.04 0-1.88.5-1.88 1.35 0 2.32 4.02.73 4.02 2.93 0 .88-.83 1.36-1.99 1.36-.99 0-1.79-.33-2.28-.93" />
            </svg>
          )}

          {canUseOperationalModules && canRead(user, 'logistica') && nav('/logistics', false, 'Logística',
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="1" y="3" width="15" height="13" rx="1"/>
              <path d="M16 8h4l3 5v3h-7V8z"/>
              <circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
            </svg>
          )}

          {canUseOperationalModules && canRead(user, 'lugares-activos') && nav('/locations', false, 'Lugares Activos',
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          )}

          {canUseOperationalModules && canReadAny(user, ['inventario.stock', 'inventario.racks', 'inventario.packs', 'inventario.incidencias']) && nav(
            canRead(user, 'inventario.stock') ? '/inventory/stock' : canRead(user, 'inventario.racks') ? '/inventory/racks' : canRead(user, 'inventario.packs') ? '/inventory/packs' : '/inventory/incidencias', false, 'Inventario',
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
              <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
              <line x1="12" y1="22.08" x2="12" y2="12"/>
            </svg>
          )}

          {canUseConfig && nav('/configuracion', false, 'Configuración',
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="4" />
              <path d="m9 3-.5 2a8 8 0 0 0-2 1.2l-2-.6-2 3.4L4 10.5a8 8 0 0 0 0 3l-1.5 1.5 2 3.4 2-.6a8 8 0 0 0 2 1.2l.5 2h4l.5-2a8 8 0 0 0 2-1.2l2 .6 2-3.4-1.5-1.5a8 8 0 0 0 0-3L20 9l-2-3.4-2 .6A8 8 0 0 0 14 5l-.5-2Z" />
            </svg>
          )}
        </nav>

        <div className="sidebar-bottom">
          {canUseClock && (
            <div className="sidebar-fixed-shortcuts">
              {nav('/rrhh/reloj', false, 'Reloj',
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="9"/>
                  <polyline points="12 7 12 12 15 14"/>
                  <path d="M5 3 2 6"/>
                  <path d="m19 3 3 3"/>
                </svg>
              )}
            </div>
          )}

          <div className="theme-switch" onClick={toggleTheme} title="Cambiar tema">
            <div className={`theme-switch-track ${theme === 'light' ? 'light' : ''}`}>
              <div className="theme-switch-thumb">
                {theme === 'dark' ? '🌙' : '☀️'}
              </div>
            </div>
            <span className="theme-switch-label">
              {theme === 'dark' ? 'Oscuro' : 'Claro'}
            </span>
          </div>

          <div className="sidebar-user">
            {user?.avatar_url
              ? <img src={user.avatar_url} alt="" className="user-avatar" />
              : <div className="user-avatar-fallback">{user?.name?.[0]?.toUpperCase()}</div>
            }
            <div className="user-info">
              <div className="user-name">{user?.name}</div>
              <div className="user-role">{roleLabel(user?.role)}</div>
            </div>
            <button onClick={handleLogout} className="btn btn-icon btn-ghost logout-btn" title="Cerrar sesión">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16,17 21,12 16,7"/><line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </button>
          </div>
        </div>
      </aside>

      {/* Tooltip flotante para desktop — se mueve con animación entre ítems */}
      <div
        className={`sidebar-tooltip${tooltip.visible ? ' sidebar-tooltip--on' : ''}`}
        style={{ top: tooltip.y }}
      >
        {tooltip.label}
      </div>

      <main className="main-content">
        {/* El botón vive dentro del contenedor que scrollea para que se vaya con
            el contenido en vez de quedar fijo tapando la pantalla. */}
        <button
          className={`mobile-menu-btn${menuOpen ? ' is-open' : ''}`}
          onClick={() => setMenuOpen(prev => { if (prev) haptic.untick(); else haptic.tick(); return !prev; })}
          aria-label="Menú"
        >
          {menuOpen ? '✕' : '☰'}
        </button>
        {/* resetKey = ruta: al navegar a otra pantalla el boundary se recupera */}
        <ErrorBoundary resetKey={location.pathname} label={location.pathname} fallback={<PageError />}>
          <Outlet />
        </ErrorBoundary>
      </main>

      {/* Triple espacio (fuera de un campo de texto) → alimentar Reglas de IA */}
      {canWrite(user, 'configuracion.ia') && <AiRulesQuickPopup />}

      {/* Aviso de deploy: cuenta regresiva + bloqueo durante la actualización */}
      <DeployNotice />
    </div>
  );
}
